import React, { useState, useEffect, useMemo } from 'react';
import { X, UserMinus, Trash2, Loader2, Check } from 'lucide-react';
import { cn } from "@/lib/utils";

// shadcn/ui components
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
    Command,
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from "@/components/ui/command";

// Zustand Store
import useCanvasStore from "../stores/canvasStore";

/**
 * BulkAssignDialog - Reads state from Zustand store
 * No props needed - all data comes from store
 */
const BulkAssignDialog = () => {
    // Zustand store
    const bulkAssignDialogOpen = useCanvasStore(s => s.bulkAssignDialogOpen);
    const setBulkAssignDialogOpen = useCanvasStore(s => s.setBulkAssignDialogOpen);
    const selectedItems = useCanvasStore(s => s.selectedItems);
    const users = useCanvasStore(s => s.users);
    const bulkAssign = useCanvasStore(s => s.bulkAssign);
    const bulkRemoveAssignees = useCanvasStore(s => s.bulkRemoveAssignees);
    const fetchUsers = useCanvasStore(s => s.fetchUsers);

    const [selectedAssignees, setSelectedAssignees] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [removedAssigneeIds, setRemovedAssigneeIds] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');

    // Fetch users when dialog opens if not already loaded
    useEffect(() => {
        if (bulkAssignDialogOpen && users.length === 0) {
            fetchUsers();
        }
    }, [bulkAssignDialogOpen, users.length, fetchUsers]);

    // Reset state when dialog opens/closes
    useEffect(() => {
        if (bulkAssignDialogOpen) {
            setRemovedAssigneeIds(new Set());
            setSelectedAssignees([]);
            setSearchQuery('');
        }
    }, [bulkAssignDialogOpen]);

    // Get all unique current assignees across selected items, excluding removed ones
    const currentAssignees = useMemo(() => {
        const assigneeMap = new Map();
        selectedItems.forEach(item => {
            const assignees = item.assignees || item.employees;
            if (assignees && Array.isArray(assignees)) {
                assignees.forEach(assignee => {
                    if (!removedAssigneeIds.has(assignee.id)) {
                        assigneeMap.set(assignee.id, assignee);
                    }
                });
            }
        });
        return Array.from(assigneeMap.values());
    }, [selectedItems, removedAssigneeIds]);

    // Filter users based on search query and exclude already selected
    const filteredUsers = useMemo(() => {
        const selectedIds = new Set(selectedAssignees.map(a => a.id));
        return users.filter(user => {
            if (selectedIds.has(user.id)) return false;
            if (!searchQuery) return true;
            const name = (user.name || user.username || `${user.first_name || ''} ${user.last_name || ''}`).toLowerCase();
            const email = (user.email || '').toLowerCase();
            const query = searchQuery.toLowerCase();
            return name.includes(query) || email.includes(query);
        });
    }, [users, selectedAssignees, searchQuery]);

    const getDisplayName = (user) => {
        return user.name || user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim();
    };

    const getInitials = (user) => {
        const name = user.name || user.username || user.first_name || '';
        return name.charAt(0).toUpperCase();
    };

    const handleAssign = async () => {
        if (selectedAssignees.length === 0) {
            return;
        }
        
        setIsLoading(true);
        try {
            await bulkAssign(selectedAssignees);
            setSelectedAssignees([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveAssignee = async (assignee) => {
        setIsLoading(true);
        try {
            await bulkRemoveAssignees([assignee]);
            // Mark as removed in local state so it disappears from UI
            setRemovedAssigneeIds(prev => new Set([...prev, assignee.id]));
        } finally {
            setIsLoading(false);
        }
    };

    const handleClearAll = async () => {
        if (currentAssignees.length === 0) return;
        
        setIsLoading(true);
        try {
            await bulkRemoveAssignees(currentAssignees);
            // Mark all as removed in local state
            const allIds = currentAssignees.map(a => a.id);
            setRemovedAssigneeIds(prev => new Set([...prev, ...allIds]));
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setSelectedAssignees([]);
            setBulkAssignDialogOpen(false);
        }
    };

    const handleSelectUser = (user) => {
        setSelectedAssignees(prev => [...prev, user]);
        setSearchQuery('');
    };

    const handleRemoveSelectedUser = (userId) => {
        setSelectedAssignees(prev => prev.filter(a => a.id !== userId));
    };

    return (
        <Dialog open={bulkAssignDialogOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="text-lg font-semibold">
                        Bulk Assign Items
                    </DialogTitle>
                    <DialogDescription>
                        Assign {selectedItems.length} selected item{selectedItems.length !== 1 ? 's' : ''} to users
                    </DialogDescription>
                </DialogHeader>
                
                <ScrollArea className="flex-1 pr-2 -mr-2">
                <div className="space-y-4 py-2">
                    {/* Selected Items Preview */}
                    <div className="rounded-lg border border-border p-3">
                        <p className="text-xs text-muted-foreground mb-2">Selected Items:</p>
                        <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                            {selectedItems.slice(0, 10).map((item) => (
                                <Badge
                                    key={item.id}
                                    variant="secondary"
                                    className="text-xs max-w-[140px] truncate"
                                >
                                    {item.name}
                                </Badge>
                            ))}
                            {selectedItems.length > 10 && (
                                <Badge variant="outline" className="text-xs">
                                    +{selectedItems.length - 10} more
                                </Badge>
                            )}
                        </div>
                    </div>

                    {/* Current Assignees */}
                    {currentAssignees.length > 0 && (
                        <>
                            <div className="rounded-lg bg-muted/50 p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm font-medium">Currently Assigned</p>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleClearAll}
                                        disabled={isLoading}
                                        className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                                    >
                                        <Trash2 className="w-3 h-3 mr-1" />
                                        Clear All
                                    </Button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {currentAssignees.map((assignee) => (
                                        <div
                                            key={assignee.id}
                                            className="inline-flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-full bg-secondary border border-border text-secondary-foreground"
                                        >
                                            <Avatar className="w-5 h-5">
                                                <AvatarImage src={assignee.picture} />
                                                <AvatarFallback className="text-[10px] bg-muted">
                                                    {getInitials(assignee)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span className="text-xs font-medium">{getDisplayName(assignee)}</span>
                                            <button
                                                onClick={() => handleRemoveAssignee(assignee)}
                                                disabled={isLoading}
                                                className="rounded-full p-0.5 hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                                            >
                                                <UserMinus className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <Separator className="w-full" />
                                </div>
                                <div className="relative flex justify-center">
                                    <span className="bg-background px-2 text-xs text-muted-foreground">
                                        Add New Assignees
                                    </span>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Selected New Assignees */}
                    {selectedAssignees.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {selectedAssignees.map((assignee) => (
                                <div
                                    key={assignee.id}
                                    className="inline-flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-full bg-primary text-primary-foreground"
                                >
                                    <Avatar className="w-5 h-5">
                                        <AvatarImage src={assignee.picture} />
                                        <AvatarFallback className="text-[10px] bg-primary-foreground text-primary">
                                            {getInitials(assignee)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <span className="text-xs font-medium">{getDisplayName(assignee)}</span>
                                    <button
                                        onClick={() => handleRemoveSelectedUser(assignee.id)}
                                        className="rounded-full p-0.5 hover:bg-primary-foreground/20 transition-colors"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* User Search/Select */}
                    <Command className="rounded-lg border border-border overflow-hidden" shouldFilter={false}>
                        <CommandInput
                            placeholder="Search users to add..."
                            value={searchQuery}
                            onValueChange={setSearchQuery}
                            disabled={isLoading}
                        />
                        <CommandList className="min-h-[200px] max-h-[200px]">
                            <CommandEmpty className="py-12 text-center text-sm text-muted-foreground">
                                No users found.
                            </CommandEmpty>
                            <CommandGroup>
                                {filteredUsers.slice(0, 8).map((user) => (
                                    <CommandItem
                                        key={user.id}
                                        value={user.id.toString()}
                                        onSelect={() => handleSelectUser(user)}
                                        className="flex items-center gap-3 py-2.5 px-3 cursor-pointer"
                                    >
                                        <Avatar className="w-8 h-8 flex-shrink-0">
                                            <AvatarImage src={user.picture} />
                                            <AvatarFallback className="text-xs bg-muted">
                                                {getInitials(user)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex flex-col min-w-0 flex-1">
                                            <span className="text-sm font-medium truncate">
                                                {getDisplayName(user)}
                                            </span>
                                            {user.email && (
                                                <span className="text-xs text-muted-foreground truncate">
                                                    {user.email}
                                                </span>
                                            )}
                                        </div>
                                        {selectedAssignees.some(a => a.id === user.id) && (
                                            <Check className="w-4 h-4 flex-shrink-0 text-primary" />
                                        )}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </div>
                </ScrollArea>

                <DialogFooter className="gap-2 sm:gap-0 pt-2">
                    <Button 
                        variant="outline" 
                        onClick={handleClose} 
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAssign}
                        disabled={selectedAssignees.length === 0 || isLoading}
                    >
                        {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {isLoading 
                            ? 'Assigning...' 
                            : `Assign ${selectedItems.length} item${selectedItems.length !== 1 ? 's' : ''} to ${selectedAssignees.length} user${selectedAssignees.length !== 1 ? 's' : ''}`
                        }
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default BulkAssignDialog;
